<script setup>
import {DocumentIcon, TrashIcon} from '@heroicons/vue/outline'
</script>

<template>
    <OnBoardingForm :title="$t('Completion - On Boarding')" :current-step="4">

        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">

            <form class="flex flex-col space-y-6" @submit.prevent="submitForm">

                <FormSection v-for="(person, personIdx) in form.additional_info.owners">
                    <template #title>
                        {{ $t('Identity documents of') }} {{ personName(personIdx) }}
                    </template>
                    <template #description>

                    </template>

                    <div class="grid gap-6 grid-cols-12">
                        <div class="col-span-6">
                            <!-- Profile Photo File Input -->
                            <input
                                :ref="`id_doc_${personIdx}_front`"
                                type="file"
                                class="hidden"
                                accept="image/*,.pdf"
                                @change="updateFile($event,person, 'passport_front')"
                            >
                            <JetLabel :for="`id_doc_${personIdx}_front`" :value="$t('Front side')"/>
                            <JetSecondaryButton class="mt-2 mr-2" type="button"
                                                @click.prevent="openFileDialog(`id_doc_${personIdx}_front`)">
                                {{ $t('Choose file') }}
                            </JetSecondaryButton>
                            <JetInputError :message="form.errors[`additional_info.owners.${personIdx}.passport_front`]"
                                           class="mt-2"/>
                            <p class="text-sm text-success-700 mt-3" v-if="person.passport_front">
                                {{ person.passport_front.name }}</p>
                            <p class="text-sm text-danger-700 mt-3" v-else>{{ $t('Please choose a file') }}</p>
                        </div>
                        <div class="col-span-6">
                            <!-- Profile Photo File Input -->
                            <input
                                :ref="`id_doc_${personIdx}_back`"
                                type="file"
                                class="hidden"
                                accept="image/*,.pdf"
                                @change="updateFile($event,person, 'passport_back')"
                            >
                            <JetLabel :for="`id_doc_${personIdx}_back`" :value="$t('Back side')"/>
                            <JetSecondaryButton class="mt-2 mr-2" type="button"
                                                @click.prevent="openFileDialog(`id_doc_${personIdx}_back`)">
                                {{ $t('Choose file') }}
                            </JetSecondaryButton>
                            <JetInputError :message="form.errors[`additional_info.owners.${personIdx}.passport_back`]"
                                           class="mt-2"/>
                            <p class="text-sm text-success-700 mt-3" v-if="person.passport_back">
                                {{ person.passport_back.name }}</p>
                            <p class="text-sm text-danger-700 mt-3" v-else>{{ $t('Please choose a file') }}</p>
                        </div>
                    </div>
                </FormSection>

                <FormSection v-for="(file, fileIdx) in form.additional_info.additionalFiles">
                    <template #title>
                        {{ fileTitle(file.name) }}
                    </template>
                    <template #description>
                        {{ fileDescription(file.name) }}
                    </template>

                    <div class="grid gap-6 grid-cols-12">
                        <div class="col-span-12">
                            <!-- Profile Photo File Input -->
                            <input
                                :ref="`add_doc_${file.name}`"
                                type="file"
                                multiple
                                class="hidden"
                                accept="image/*,.pdf"
                                @change="addFiles($event,file, 'files')"
                            >
                            <JetLabel :for="`add_doc_${file.name}`" :value="$t('Selected files')"/>
                            <div class="divide-y py-2">
                                <template v-if="file.files">
                                    <div v-for="(fileLine, fileIdx) in file.files"
                                         class="py-2 flex items-center text-success-700">
                                        <DocumentIcon class="w-3 h-3 mr-2" aria-hidden="true"/>
                                        <span class="grow">
                                           {{ fileLine.name }}
                                        </span>
                                        <span class="shrink">
                                            <TrashIcon class="w-4 h-4 mx-2 cursor-pointer text-danger-500"
                                                       @click.prevent="removeFile(file, 'files', fileIdx)"
                                                       aria-hidden="true"/>
                                        </span>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="text-danger-700" v-if="!file.no_file_comment">
                                        {{ $t('Please select documents for upload') }}<br/>
                                        {{ $t('or fill in the "No documents" section.') }}
                                    </div>
                                    <div v-else class="text-success-800">
                                        {{ $t('Reason provided') }} - <span @click.prevent="openFileDialog(`add_doc_${file.name}`)"
                                                                class="font-bold cursor-pointer">{{ $t('Choose documents instead?') }}</span>
                                    </div>
                                </template>
                            </div>
                            <div class="mt-2 flex justify-between items-center" v-if="file.files || !file.no_file_comment">
                                <JetSecondaryButton class="mr-2" type="button"
                                                    @click.prevent="openFileDialog(`add_doc_${file.name}`)">
                                    <span v-if="file.files">{{ $t('Add documents') }}</span>
                                    <span v-else>{{ $t('Choose documents') }}</span>
                                </JetSecondaryButton>
                                <span v-if="file.files" class="text-danger-700 underline cursor-pointer"
                                      @click.prevent="file.files = null">{{ $t('Clear list') }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="col-span-12 border-t border-gray-200 mt-4" v-if="!file.files">
                        <h5 class="text-lg text-primary-600 font-bold mt-3 mb-4">{{ $t('Do you have no documents on this topic?') }}</h5>
                        <JetLabel :for="`add_doc_comment_${file.name}`" :value="$t('Tell us the reason')"/>
                        <JetTextArea v-model="file.no_file_comment" class="w-full mt-1" rows="4"></JetTextArea>
                        <JetInputError
                            :message="form.errors[`additional_info.additionalFiles.${fileIdx}.no_file_comment`]"
                            class="mt-2"/>
                    </div>
                </FormSection>

                <FormSection>
                    <template #title>
                        {{ $t('Knowledge of financial products and their risks') }}
                    </template>
                    <template #description>
                        {{ $t('Please confirm all points to complete the process.') }}
                    </template>

                    <div class="flex flex-col space-y-6">
                        <div class="flex space-x-2">
                            <JetCheckbox id="consent_question_1" v-model="form.consent_risk_1"></JetCheckbox>
                            <div>
                                <JetLabel for="consent_question_1"
                                          :value="$t('consent_risk_1')"/>
                                <JetInputError :message="form.errors[`consent_risk_1`]" class="mt-2"/>
                            </div>
                        </div>
                        <div class="flex space-x-2">
                            <JetCheckbox id="consent_question_2" v-model="form.consent_risk_2"></JetCheckbox>
                            <div>
                                <JetLabel for="consent_question_2"
                                          :value="$t('consent_risk_2')"/>
                                <JetInputError :message="form.errors[`consent_risk_2`]" class="mt-2"/>
                            </div>
                        </div>
                    </div>
                </FormSection>

                <div class="flex justify-end">
                    <JetActionMessage :on="form.recentlySuccessful" class="mr-3">
                        {{ $t('Saved.') }}
                    </JetActionMessage>

                    <JetButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        {{ $t('Complete process') }}
                    </JetButton>
                </div>
            </form>
        </div>

    </OnBoardingForm>
</template>

<script>
import OnBoardingForm from '@/Layouts/OnBoardingForm.vue';
import FormSection from "@/Components/Form/FormSection";
import Label from '@/Components/Form/Label';
import JetButton from "@/Jetstream/Button";
import JetActionMessage from "@/Jetstream/ActionMessage";
import JetInputError from "@/Jetstream/InputError";
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetTextArea from '@/Jetstream/Textarea.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import {defineComponent} from "vue";

export default defineComponent({
    components: {
        OnBoardingForm,
        FormSection,
        JetInputError,
        Label,
        JetButton,
        JetActionMessage,
        JetSecondaryButton,
        JetLabel,
        JetTextArea,
        JetCheckbox,
    },
    computed: {},
    props: {
        accountOwners: Array,
        displayedFileInputs: Object,
    },
    data() {
        return {
            form: this.$inertia.form({
                additional_info: this.getDefaultFormStructure(),
                consent_risk_1: false,
                consent_risk_2: false,
            }),
            fileSections: {
                'real_estate_yields': {
                    title: 'file_section_real_estate_yields_title',
                    description: 'file_section_real_estate_yields_desc',
                },
                'real_estate_sales': {
                    title: 'file_section_real_estate_sales_title',
                    description: 'file_section_real_estate_sales_desc',
                },
                'inheritance': {
                    title: 'file_section_inheritance_title',
                    description: 'file_section_inheritance_desc',
                },
                'gift': {
                    title: 'file_section_gift_title',
                    description: 'file_section_gift_desc',
                },
                'others': {
                    title: 'file_section_others_title',
                    description: 'file_section_others_desc',
                },
            }
        };
    },
    methods: {
        submitForm() {
            this.form.post(route('onboarding.step4.store'), {
                preserveScroll: false,
                preserveState: true,
            })
        },

        updateFile(evt, entity, attribute) {
            if (!evt.target.files[0])
                return;

            entity[attribute] = evt.target.files[0];
        },

        addFiles(evt, entity, attribute) {
            if (!evt.currentTarget || !evt.currentTarget.files || !evt.currentTarget.files.length)
                return;

            Array.from(evt.currentTarget.files).forEach(file => this.addFile(file, entity, attribute));

            entity.no_file_comment = '';
            this.$refs[`add_doc_${entity.name}`].value = '';
        },

        addFile(file, entity, attribute) {
            if (!entity[attribute]) entity[attribute] = [];
            entity[attribute].push(file);
        },

        removeFile(entity, attribute, idx) {
            entity[attribute].splice(idx, 1);
            if(entity[attribute].length == 0) entity[attribute] = null;
        },

        openFileDialog(refKey) {
            if (!this.$refs[refKey])
                return;

            this.$refs[refKey][0].click();
        },

        fileTitle(key) {
            if (this.fileSections && this.fileSections[key]) {
                return this.$t(this.fileSections[key].title);
            }
        },

        fileDescription(key) {
            if (this.fileSections && this.fileSections[key]) {
                return this.$t(this.fileSections[key].description);
            }
        },

        personName(personIdx) {
            return this.accountOwners[personIdx].firstname + ' ' + this.accountOwners[personIdx].lastname;
        },

        getDefaultFormStructure() {
            let formInputs = {
                owners: [],
                additionalFiles: [],
            };

            this.accountOwners.forEach((owner, ownerIdx) => {
                formInputs.owners.push(this.getEmptyOwnerFilesLine());
            });

            Object.values(this.displayedFileInputs).forEach((input, inputIdx) => {
                formInputs.additionalFiles.push(this.getEmptyAdditionalFileLine(input));
            });

            return formInputs;
        },

        getEmptyAdditionalFileLine(name) {
            return {
                name,
                files: null,
                no_file_comment: null,
            }
        },

        getEmptyOwnerFilesLine() {
            return {
                passport_front: null,
                passport_back: null,
            }
        }
    }

});
</script>
